import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Copy,
  Loader2,
  Mail,
  MessageCircle,
  Share2,
  Sparkles,
  Ticket,
} from "lucide-react";
import { toast } from "sonner";
import { createReferral } from "@/lib/referrals";

const CURRENCY_HINT_INR = "₹";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function buildMessage({ courseTitle, referrerName, bonusInr, shareUrl }) {
  return `Hey! I'm learning ${courseTitle} on Knowledge Sharing — Key to Success. Join with my link and we both get ${CURRENCY_HINT_INR}${bonusInr} in credit. ${shareUrl}`.trim();
}

/**
 * Modal for a paid student to mint a referral link and share it via
 * WhatsApp / email / copy-to-clipboard.
 *
 * `eligibleCourses` is a list of ``{id, title, referrer_bonus_inr?}`` objects —
 * only courses the caller is paid + active on.
 */
export default function ReferAFriendDialog({
  open,
  onOpenChange,
  eligibleCourses,
  referrerName,
  defaultCourseId,
  onCreated,
}) {
  const [courseId, setCourseId] = useState("");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null); // { id, share_url, share_token, expires_at }

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setCourseId(defaultCourseId || eligibleCourses?.[0]?.id || "");
  }, [open, defaultCourseId, eligibleCourses]);

  const selectedCourse = useMemo(
    () => eligibleCourses?.find((c) => c.id === courseId),
    [eligibleCourses, courseId]
  );

  const generate = async () => {
    if (!courseId) {
      toast.error("Pick a course first");
      return;
    }
    setCreating(true);
    try {
      const r = await createReferral(courseId);
      setResult(r);
      onCreated?.(r);
      toast.success("Share link ready");
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed to create referral";
      if (d === "not_paid_on_course") {
        toast.error("You need a paid, active enrollment on that course.");
      } else if (d === "course_not_found") {
        toast.error("Course not found.");
      } else {
        toast.error(typeof d === "string" ? d : "Failed");
      }
    } finally {
      setCreating(false);
    }
  };

  const bonusInr = Math.round(selectedCourse?.referrer_bonus_inr || 100);
  const shareMessage = result
    ? buildMessage({
        courseTitle: selectedCourse?.title || "a course",
        referrerName,
        bonusInr,
        shareUrl: result.share_url,
      })
    : "";

  const copyLink = async () => {
    if (!result?.share_url) return;
    try {
      await navigator.clipboard.writeText(result.share_url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — long-press the field to select");
    }
  };

  const whatsappHref = result
    ? `https://wa.me/?text=${encodeURIComponent(shareMessage)}`
    : "#";
  const mailHref = result
    ? `mailto:?subject=${encodeURIComponent(
        "Learn with me on KKS"
      )}&body=${encodeURIComponent(shareMessage)}`
    : "#";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="refer-a-friend-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-amber-600" />
            Refer a friend
          </DialogTitle>
          <DialogDescription>
            Share your learning journey. When a friend signs up and pays,
            you both get credit toward future purchases.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Course</Label>
            <Select
              value={courseId}
              onValueChange={setCourseId}
              disabled={!!result}
            >
              <SelectTrigger data-testid="refer-course-select">
                <SelectValue placeholder="Pick a course you've paid for" />
              </SelectTrigger>
              <SelectContent>
                {(eligibleCourses || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {eligibleCourses && eligibleCourses.length === 0 && (
              <p className="text-xs text-stone-500">
                No eligible courses yet — pay for a course first to unlock referrals.
              </p>
            )}
          </div>

          {selectedCourse && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
              data-testid="refer-reward-preview"
            >
              <Ticket className="h-3.5 w-3.5 mt-0.5" />
              <div>
                You&apos;ll get{" "}
                <span className="font-semibold">
                  ₹{Math.round(selectedCourse.referrer_bonus_inr || 100)}
                </span>{" "}
                and your friend gets{" "}
                <span className="font-semibold">
                  ₹{Math.round(selectedCourse.referred_bonus_inr || 50)}
                </span>{" "}
                in credit once they enroll and pay.
              </div>
            </div>
          )}

          {!result ? (
            <Button
              onClick={generate}
              disabled={creating || !courseId}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="refer-generate-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Preparing…
                </>
              ) : (
                <>
                  <Sparkles className="mr-1 h-4 w-4" />
                  Generate share link
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-stone-500">Your unique link</Label>
                <Input
                  value={result.share_url}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="refer-share-link"
                  onFocus={(e) => e.target.select()}
                />
                <p className="text-xs text-stone-500">
                  Expires {fmtDate(result.expires_at)}.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  onClick={copyLink}
                  data-testid="refer-copy-btn"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                </Button>
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex"
                  data-testid="refer-whatsapp-btn"
                >
                  <Button
                    variant="outline"
                    className="w-full border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  >
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                </a>
                <a
                  href={mailHref}
                  className="inline-flex"
                  data-testid="refer-email-btn"
                >
                  <Button variant="outline" className="w-full">
                    <Mail className="h-3.5 w-3.5 mr-1" /> Email
                  </Button>
                </a>
              </div>
              <Badge
                variant="outline"
                className="bg-stone-50 text-stone-600 border-stone-200"
              >
                Referral ID {result.id.slice(0, 8)}
              </Badge>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
